/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
import { useEffect, useRef, useState } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Button from '@deprecated-ui/common/Button';
import color from '@deprecated-ui/_style/color';
import { deleteImageExif, getImageDimensions } from 'utils/image';
import EmojiPicker from 'global/Pickers/EmojiPicker';
import GifPicker from 'global/Pickers/GifPicker';
import ERROR_MESSAGE from 'constants/errorMessage';
import alertMessage from 'global/AlertMessage';
import Image from 'global/Image';
import {
  COMPOSE_MODE,
  ALLOWED_DOC_EXT,
  MAX_FILE_SIZE,
  IMAGE_TYPE_GIF,
  IMAGE_TYPE_COMMON,
  MAX_IMAGE_SIZE,
  MAX_FILE_SIZE_MB,
} from '../constants';
import {
  ButtonSubmit,
  ButtonPinned,
  EmojiContainer,
  FooterContainer,
  GifContainer,
} from './styled';
import IconFooter from './IconFooter';
import { IImageItem } from '../types';

interface FooterProps {
  onCancel: () => any;
  onSubmit: (val: any) => Promise<any>;
  onSelectEmoji: (val: any) => any;
  onSelectGif: (val: any) => any;
  onTogglePolling: () => any;
  onToggleTargetPrice: () => any;
  isLoading: boolean;
  mode: string;
  isOpenPolling: boolean;
  documents: Array<any>;
  onSetDocuments: (val: any) => any;
  onSetImages: (val: any) => any;
  images: IImageItem[];
  onErrorMaxSize: (val: any) => void;
  isSubmitDisabled: boolean;
  isPostComment?: boolean;
  isPinned?: boolean;
  onTogglePin: () => any;
  showPinButton?: boolean;
}

function Footer({
  mode,
  onCancel,
  onSubmit,
  isLoading,
  onSelectEmoji,
  onSelectGif,
  onTogglePolling,
  onToggleTargetPrice,
  isOpenPolling,
  documents,
  onSetDocuments,
  onSetImages,
  images,
  onErrorMaxSize,
  isSubmitDisabled,
  isPostComment,
  isPinned,
  onTogglePin,
  showPinButton,
}: FooterProps) {
  const pictureRef = useRef(null);
  const documentRef = useRef(null);
  const emojiContainerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const gifContainerRef = useRef(null);
  const gifButtonRef = useRef(null);

  const [showEmoji, setShowEmoji] = useState(false);
  const [showGif, setShowGif] = useState(false);

  const openPictureAttachment = () => {
    setShowEmoji(false);
    if (images.length <= 10) {
      pictureRef.current.click();
    } else {
      alertMessage({
        content: 'Max Allowed Upload Images is 10 Images',
        alertType: 'plain-default',
        messageType: 'error',
      });
    }
  };

  const openDocumentAttachment = () => {
    setShowEmoji(false);
    documentRef.current.click();
  };

  const processImages = async (imageFiles) =>
    Promise.all(
      imageFiles.map(
        async (imageFile) =>
          new Promise((resolve) => {
            const isImageAvailable =
              images.findIndex((image) => image.fileName === imageFile?.name) >
              -1;
            if (isImageAvailable) {
              resolve(false);
            }

            const reader = new FileReader();
            reader.readAsArrayBuffer(imageFile);

            reader.onloadend = async (e) => {
              const imageType = imageFile.type;
              if (imageFile.size > MAX_IMAGE_SIZE) {
                onErrorMaxSize(true);
                resolve(false);
              }
              const imagePreviewUrl = URL.createObjectURL(imageFile);
              const dimensions = await getImageDimensions(imagePreviewUrl);
              const imageData: IImageItem = {
                isNew: true,
                type: IMAGE_TYPE_COMMON,
                imagePreviewUrl,
                dimensions,
                fileName: imageFile?.name,
              };
              if (imageType === 'image/jpeg' || imageType === 'image/jpg') {
                // in some case some image form not have
                // format that can be deleted exif, so we try catch this block
                // to prevent user cannot upload the image
                try {
                  // @ts-ignore
                  const deletedExif = deleteImageExif(
                    e.target.result,
                    imageType,
                  );
                  imageData.file = deletedExif;
                } catch (err) {
                  imageData.file = imageFile;
                }
              } else {
                imageData.file = imageFile;
              }
              resolve(imageData);
            };
          }),
      ),
    );

  const handleChangePicture = async (e) => {
    e.preventDefault();
    const selectedFiles = e.target.files;
    const imageFiles = Array.from(selectedFiles);
    const processedImages = await processImages(imageFiles);
    const validImages = processedImages.filter((image) => image);

    const hasGifImage = images.find((data) => data.type === IMAGE_TYPE_GIF);
    let oldImages = images;
    if (hasGifImage) {
      oldImages = [];
    }

    const listImage = [...oldImages, ...validImages];

    // prevent upload image more than 10
    const newListImage =
      listImage.length > 10 ? listImage.slice(0, 10) : listImage;

    onSetImages(newListImage);
    e.target.value = null;
  };

  const handleChangeDocument = (e) => {
    e.preventDefault();
    const documentFiles = [...documents];
    let sameFileCounter = 0;

    // validate documents
    Array.from(e.target.files).forEach((fileTarget: any) => {
      // Reference: [web legacy]/app/components/stream/smart/ComposeStream.js
      // line: 489 - 528

      if (fileTarget.type.match('image.*')) {
        alertMessage({
          content: ERROR_MESSAGE.DOCUMENT_IMAGE_NOT_ALLOWED,
          alertType: 'default',
          messageType: 'error',
          hasCloseIcon: false,
        });
        return false;
      }

      // Max document upload size 20 MB = 20971520 bytes.
      if (fileTarget.size > MAX_FILE_SIZE) {
        alertMessage({
          content: `File too large. The maximum size is ${MAX_FILE_SIZE_MB}MB`,
          alertType: 'plain-default',
          messageType: 'error',
          hasCloseIcon: false,
        });
        return false;
      }

      // If the file is unique, push into upload file stack
      const fileInStack = documentFiles.filter(
        (doc) => doc.name === fileTarget.name,
      );
      if (fileInStack.length === 0 || documentFiles.length === 0) {
        documentFiles.push(fileTarget);
      } else {
        sameFileCounter += 1;
      }
    });

    if (
      e.target.files.length + documents.length - sameFileCounter > 1 ||
      e.target.files.length > 1
    ) {
      return alertMessage({
        content: ERROR_MESSAGE.MAX_UPLOAD_FILES,
        alertType: 'default',
        messageType: 'error',
        hasCloseIcon: false,
      });
    }

    if (documentFiles.length > 0 && documentFiles.length < 2) {
      onSetDocuments(documentFiles);
    }

    // reset input file
    // eslint-disable-next-line no-param-reassign
    e.target.value = null;
  };

  const handleSelectEmoji = (emoji) => {
    onSelectEmoji(emoji);
  };

  const handleSelectGif = (gifData) => {
    const hasCommonImage = images.find(
      (data) => data.type === IMAGE_TYPE_COMMON,
    );
    let oldFile = images;
    if (hasCommonImage) {
      oldFile = [];
    }
    const newGifData: IImageItem = {
      file: gifData.post,
      imagePreviewUrl: gifData.preview,
      isNew: true,
      type: IMAGE_TYPE_GIF,
    };
    onSelectGif([...oldFile, newGifData]);
    setShowGif(false);
  };

  const onClickGifButton = () => {
    setShowGif((prev) => !prev);
    setShowEmoji(false);
  };

  const onClickEmojiButton = () => {
    setShowEmoji((prev) => !prev);
    setShowGif(false);
  };

  const handleClickOutsideEmoji = (event) => {
    const isClickButtonEmoji =
      emojiButtonRef.current && !emojiButtonRef.current?.contains(event.target);
    const isClickResultEmoji =
      emojiContainerRef.current &&
      !emojiContainerRef.current?.contains(event.target);
    if (isClickButtonEmoji && isClickResultEmoji) {
      onClickEmojiButton();
    }
  };

  const handleClickOutsideGif = (event) => {
    const isClickButtonGif =
      gifButtonRef.current && !gifButtonRef.current?.contains(event.target);
    const isClickResultGif =
      gifContainerRef.current &&
      !gifContainerRef.current?.contains(event.target);
    if (isClickButtonGif && isClickResultGif) {
      setShowGif(false);
    }
  };

  const onClickTooglePolling = () => {
    onTogglePolling();
    setShowEmoji(false);
  };

  const onClickToggleTargetPrice = () => {
    onToggleTargetPrice();
    setShowEmoji(false);
  };

  const handleClickCancel = () => {
    setShowEmoji(false);
    setShowGif(false);
    onCancel();
  };

  const handleClickSubmit = (e) => {
    onSubmit(e);
    setShowEmoji(false);
    setShowGif(false);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideGif, true);
    document.addEventListener('click', handleClickOutsideEmoji, true);
    return () => {
      document.removeEventListener('click', handleClickOutsideGif, true);
      document.removeEventListener('click', handleClickOutsideEmoji, true);
    };
  }, []);

  const showPollingButton = mode === COMPOSE_MODE.COMPOSE;

  const iconGif = `/icon/compose-gif${showGif ? '-active' : ''}.svg`;
  const iconEmoji = `/icon/compose-smiley${showEmoji ? '-active' : ''}.svg`;
  const iconPinned = `/icon/compose-pinned${isPinned ? '-active' : ''}.svg`;

  return (
    <div>
      {showEmoji && (
        <EmojiContainer ref={emojiContainerRef}>
          <EmojiPicker onSelectEmoji={handleSelectEmoji} />
        </EmojiContainer>
      )}
      {showGif && (
        <GifContainer ref={gifContainerRef}>
          <GifPicker onSelectGif={handleSelectGif} gifColumns={5} />
        </GifContainer>
      )}
      {/* @ts-expect-error */}
      <FooterContainer repost={mode === COMPOSE_MODE.REPOST}>
        <Flex>
          <IconFooter
            onClick={openPictureAttachment}
            title="Image"
            src="/icon/compose-image.svg"
            alt="compose-image"
            isVisible={!isOpenPolling}
          />
          <IconFooter
            onClick={openDocumentAttachment}
            title="Attachment"
            src="/icon/compose-attach.svg"
            alt="compose-attachment"
            isVisible={!isOpenPolling}
          />
          <IconFooter
            onClick={onClickEmojiButton}
            title="Emoji"
            src={iconEmoji}
            alt="compose-emoji"
            iconRef={emojiButtonRef}
          />
          <IconFooter
            onClick={onClickGifButton}
            title="Gif"
            src={iconGif}
            alt="compose-gif"
            isVisible={!isOpenPolling}
            iconRef={gifButtonRef}
          />
          <IconFooter
            onClick={onClickTooglePolling}
            title="Poling"
            src="/icon/compose-pols.svg"
            alt="compose-pols"
            isVisible={showPollingButton}
          />
          <IconFooter
            onClick={onClickToggleTargetPrice}
            title="Target"
            src="/icon/compose-target.svg"
            alt="compose-target"
            isVisible={mode !== COMPOSE_MODE.SHARE_TEXT}
          />
        </Flex>
        <Flex>
          {showPinButton && (
            <ButtonPinned
              type="borderless"
              htmlType="button"
              height="28px"
              onClick={onTogglePin}
            >
              <Image
                src={iconPinned}
                alt="compose-pin"
                className="compose-pin-icon"
              />
              <Text color={color.midgray} size="12px">
                Pin this post
              </Text>
            </ButtonPinned>
          )}
          <Button
            type="borderless"
            htmlType="button"
            height="28px"
            onClick={handleClickCancel}
            disabled={isLoading}
          >
            <Text color={color.primary} family="bold" size="12px">
              Cancel
            </Text>
          </Button>
          <ButtonSubmit
            type="primary"
            htmlType="submit"
            height="28px"
            onClick={handleClickSubmit}
            disabled={isSubmitDisabled}
            loading={isLoading}
          >
            <Text color="inherit" family="bold" size="12px">
              Post
            </Text>
          </ButtonSubmit>
        </Flex>
        <input
          type="file"
          ref={pictureRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleChangePicture}
          multiple={!isPostComment}
        />
        <input
          type="file"
          ref={documentRef}
          style={{ display: 'none' }}
          accept={ALLOWED_DOC_EXT.join(',')}
          onChange={handleChangeDocument}
          multiple
        />
      </FooterContainer>
    </div>
  );
}

export default Footer;
